import React, { useEffect, useMemo, useState } from "react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Progress,
  Spinner,
} from "reactstrap";

import {
  SUGGESTED_CATALOG_CATEGORY_KEYS,
  suggestedCatalogCategorySortOrder,
  SuggestedCatalogCategoryKey,
} from "../../../helpers/catalogCategoryGrouping";
import { createCatalogCategory } from "../../../services/catalogCategoryService";
import { updateSettings } from "../../../services/tenantSettingService";

interface SuggestedCategoryDraft {
  key: SuggestedCatalogCategoryKey;
  enabled: boolean;
  name: string;
}

interface CatalogCategoryWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const buildInitialDrafts = (
  t: (key: string) => string
): SuggestedCategoryDraft[] =>
  SUGGESTED_CATALOG_CATEGORY_KEYS.map((key) => ({
    key,
    enabled: true,
    name: t(`studio.catalog.onboarding.suggested.${key}`),
  }));

const CatalogCategoryWizard = ({ isOpen, onClose, onComplete }: CatalogCategoryWizardProps) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [toggleEnabled, setToggleEnabled] = useState(false);
  const [drafts, setDrafts] = useState<SuggestedCategoryDraft[]>(() => buildInitialDrafts(t));

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setActiveStep(1);
    setSubmitting(false);
    setToggleEnabled(true);
    setDrafts(buildInitialDrafts(t));
  }, [isOpen, t]);

  const progressValue = useMemo(() => {
    if (activeStep <= 1) {
      return 0;
    }

    if (activeStep === 2) {
      return 50;
    }

    return 100;
  }, [activeStep]);

  const selectedDrafts = drafts.filter((draft) => draft.enabled && draft.name.trim() !== "");

  const updateDraft = (key: SuggestedCatalogCategoryKey, patch: Partial<SuggestedCategoryDraft>) => {
    setDrafts((current) =>
      current.map((draft) => (draft.key === key ? { ...draft, ...patch } : draft))
    );
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }

    onClose();
  };

  const activateCategoriesToggle = async () => {
    const response = await updateSettings({ use_catalog_categories: toggleEnabled });
    setToggleEnabled(response.data.use_catalog_categories);
    return response;
  };

  const createSuggestedCategories = async () => {
    await Promise.all(
      selectedDrafts.map((draft) =>
        createCatalogCategory({
          name: draft.name.trim(),
          sort_order: suggestedCatalogCategorySortOrder[draft.key],
          is_active: true,
        })
      )
    );
  };

  const handleNextFromStep1 = async () => {
    setSubmitting(true);

    try {
      const response = await activateCategoriesToggle();
      toast.success(response.message);
      setActiveStep(2);
    } catch (error: any) {
      toast.error(error?.data || t("studio.catalog.onboarding.toast.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleNextFromStep2 = async () => {
    if (selectedDrafts.length === 0) {
      toast.error(t("studio.catalog.onboarding.validation.categoryRequired"));
      return;
    }

    setSubmitting(true);

    try {
      await createSuggestedCategories();
      setActiveStep(3);
    } catch (error: any) {
      toast.error(error?.data || t("studio.catalog.onboarding.toast.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinish = () => {
    onComplete();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>
        {t("studio.catalog.onboarding.wizardTitle")}
      </ModalHeader>
      <ModalBody>
        <div className="progress-nav mb-4">
          <Progress value={progressValue} style={{ height: "1px" }} />
          <Nav className="nav-pills progress-bar-tab custom-nav" role="tablist">
            {[1, 2, 3].map((step) => (
              <NavItem key={step}>
                <NavLink
                  to="#"
                  tag="button"
                  type="button"
                  className={classnames("rounded-pill", {
                    active: activeStep === step,
                    done: activeStep > step,
                  })}
                  disabled
                >
                  {step}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>

        {activeStep === 1 ? (
          <div>
            <h5 className="mb-2">{t("studio.catalog.onboarding.step1.title")}</h5>
            <p className="text-muted">{t("studio.catalog.onboarding.step1.description")}</p>
            <div className="form-check form-switch">
              <Input
                id="catalog-category-wizard-toggle"
                type="switch"
                role="switch"
                checked={toggleEnabled}
                onChange={(event) => setToggleEnabled(event.target.checked)}
                disabled={submitting}
              />
              <Label htmlFor="catalog-category-wizard-toggle" className="form-check-label">
                {t("studio.catalog.onboarding.step1.toggleLabel")}
              </Label>
            </div>
            <small className="text-muted d-block mt-2">
              {t("studio.catalog.onboarding.step1.hint")}
            </small>
          </div>
        ) : null}

        {activeStep === 2 ? (
          <div>
            <h5 className="mb-2">{t("studio.catalog.onboarding.step2.title")}</h5>
            <p className="text-muted">{t("studio.catalog.onboarding.step2.description")}</p>
            <Form className="d-flex flex-column gap-3">
              {drafts.map((draft) => (
                <div className="d-flex align-items-center gap-2" key={draft.key}>
                  <Input
                    id={`catalog-category-draft-${draft.key}`}
                    type="checkbox"
                    checked={draft.enabled}
                    onChange={(event) =>
                      updateDraft(draft.key, { enabled: event.target.checked })
                    }
                    disabled={submitting}
                  />
                  <Input
                    type="text"
                    value={draft.name}
                    onChange={(event) => updateDraft(draft.key, { name: event.target.value })}
                    disabled={!draft.enabled || submitting}
                    aria-label={t(`studio.catalog.onboarding.suggested.${draft.key}`)}
                  />
                </div>
              ))}
            </Form>
          </div>
        ) : null}

        {activeStep === 3 ? (
          <div className="text-center py-2">
            <div className="avatar-md mx-auto mb-3">
              <span className="avatar-title bg-success-subtle text-success rounded-circle fs-2">
                <i className="ri-check-line" />
              </span>
            </div>
            <h5 className="mb-2">{t("studio.catalog.onboarding.step3.title")}</h5>
            <p className="text-muted mb-0">{t("studio.catalog.onboarding.step3.description")}</p>
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        <div>
          {activeStep === 3 ? (
            <Button color="light" tag={Link} to="/settings/catalog-categories" onClick={handleFinish}>
              {t("studio.catalog.onboarding.step3.manageLink")}
            </Button>
          ) : null}
        </div>
        <div className="d-flex gap-2">
          <Button color="light" onClick={handleClose} disabled={submitting}>
            {t("studio.catalog.onboarding.actions.cancel")}
          </Button>
          {activeStep === 1 ? (
            <Button color="primary" onClick={handleNextFromStep1} disabled={submitting || !toggleEnabled}>
              {submitting ? <Spinner size="sm" className="me-1" /> : null}
              {t("studio.catalog.onboarding.actions.continue")}
            </Button>
          ) : null}
          {activeStep === 2 ? (
            <Button color="primary" onClick={handleNextFromStep2} disabled={submitting}>
              {submitting ? <Spinner size="sm" className="me-1" /> : null}
              {t("studio.catalog.onboarding.actions.continue")}
            </Button>
          ) : null}
          {activeStep === 3 ? (
            <Button color="primary" onClick={handleFinish}>
              {t("studio.catalog.onboarding.actions.finish")}
            </Button>
          ) : null}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default CatalogCategoryWizard;
